import { useMemo } from "react";
import { trpc } from "@/app/api/trpcClient";

/**
 * Hook to fetch avatar data for a list of person IDs
 * Returns a map of personId -> avatar URL
 */
export function useAvatars(personIds: string[]) {
  // Sort IDs for stable cache key
  const sortedIds = useMemo(() => {
    return [...personIds].sort();
  }, [personIds]);

  const { data: avatarMap } = trpc.dashboard.getAvatars.useQuery(
    { personIds: sortedIds },
    {
      enabled: sortedIds.length > 0,
      staleTime: 10 * 60 * 1000, // 10 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes
      refetchOnWindowFocus: false,
    },
  );

  return avatarMap ?? {};
}

/**
 * Hook to get the first available avatar URL for a member
 * Used by Avatar component and other places that need a single avatar
 */
export function useAvatar(notionUserIds?: string[]) {
  const personIds = useMemo(() => notionUserIds ?? [], [notionUserIds]);
  const avatarMap = useAvatars(personIds);

  // Find the first available avatar
  const avatarUrl = useMemo(() => {
    for (const userId of personIds) {
      if (avatarMap[userId]) {
        return avatarMap[userId];
      }
    }
    return null;
  }, [avatarMap, personIds]);

  return avatarUrl;
}
